import { useMemo } from "react";
import "./Button.css";
import { Icon } from "@/components/icons/Icon";
import type { IconName } from "@/components/icons/icons";
import { cn } from "@/lib/styles.utils";

export interface ButtonProps {
  label: string;
  type:
    | "primary-black"
    | "primary-red"
    | "primary-white"
    | "secondary-black"
    | "secondary-red"
    | "secondary-white"
    | "terciary-black"
    | "terciary-red"
    | "link";
  size?: "small" | "medium" | "large";
  width?: string;
  disabled?: boolean;
  onClick?: (() => void) | ((data: any) => void);
  icon?: IconName;
  iconPosition?: "left" | "right";
  iconClassName?: string;
  buttonType?: "button" | "submit" | "reset";
}

export const Button = ({
  size = "large",
  label,
  disabled = false,
  type = "primary-red",
  onClick = () => {},
  width,
  icon,
  iconPosition,
  iconClassName = "",
  buttonType = "button",
}: ButtonProps) => {
  const iconColor = useMemo(() => {
    switch (type) {
      case "primary-black":
        return "text-ib-white";
      case "primary-red":
        return "text-ib-white";
      case "primary-white":
        if (disabled) return "text-ib-gray-disabled";
        return "text-ib-red-main group-hover:text-ib-red-dark";
      case "secondary-black":
        if (disabled) return "text-ib-gray-light-1";
        return "text-ib-black group-hover:text-ib-gray-dark-2";
      case "secondary-red":
        if (disabled) return "ib-gray-light-1";
        return "ib-red-main group-hover:text-ib-red-dark";
      case "terciary-black":
        if (disabled) return "ib-gray-light-1";
        return "ib-black group-hover:text-ib-gray-dark-2";
      case "terciary-red":
        if (disabled) return "ib-gray-light-1";
        return "ib-red-main group-hover:text-ib-red-dark";
      case "link":
        if (disabled) return "ib-gray-light-1";
        return "ib-black group-hover:text-ib-gray-dark-2";
    }
  }, [type, disabled]);

  return (
    <button
      data-testid={`button-${type}`}
      type={buttonType}
      className={`flex group items-center ${
        iconPosition === "right" ? "flex-row-reverse" : "flex-row"
      } ${size}-button ${type}-button`}
      disabled={disabled}
      onClick={onClick}
      style={{ width: width || "100%" }}
    >
      {icon ? (
        <div className={cn(iconColor)}>
          <Icon name={icon} className={cn("size-4", iconClassName)} />
        </div>
      ) : null}
      <span className="text-nowrap">{label}</span>
    </button>
  );
};

export default Button;
